<template>
  <div class="w-full">
    <div
      class="gap-size-8 bg-bg-bg-innerframe p-padding-16 flex w-full flex-col items-center rounded-md p-4"
    >
      <span class="text-font-14">인증번호 전송 이메일 주소</span>
      <span class="text-font-18 font-semibold">{{ userStore.email }}</span>
      <span class="text-font-14">10분 내에 입력해주세요</span>
    </div>
    <div>
      <div class="gap-size-12 mt-size-12 flex items-center">
        <BaseInput
          v-for="(value, index) in inputValues"
          :key="index"
          :ref="(el: any) => (inputRefs[index] = el)"
          :modelValue="inputValues[index]"
          size="md"
          variant="number"
          textAlign="center"
          :maxLength="maxLength"
          @update:modelValue="handleInput(index, $event)"
          @keydown="handleKeydown(index, $event)"
          @paste="handlePaste(index, $event)"
        />
      </div>
      <div class="mt-size-8 flex items-center justify-end">
        <Countdown
          ref="countdownRef"
          :duration="600"
          format="mm:ss"
          @finished="handleCountdownFinished"
        />
      </div>
    </div>
    <div
      class="mt-size-12 border-primary-primary800 bg-primary-primary100 p-size-8 gap-size-4 flex items-start rounded-md border"
    >
      <BaseIcon name="info" size="sm" color="var(--chips-status-pending-text)" />
      <div class="text-font-12 text-[var(--chips-status-pending-text)]">
        <p>인증번호는 6자리 숫자이며, 유효기간은 10분입니다.</p>
        <p>메일이 도착하지 않았다면 스팸함을 확인해주세요.</p>
      </div>
    </div>
  </div>
  <div class="gap-size-8 flex w-full flex-col items-center">
    <div class="gap-size-8 flex w-full items-center">
      <BaseButton
        size="lg"
        label="인증번호 확인"
        variant="contained"
        color="primary"
        :disabled="inputValues.some((value: string) => value === '')"
        full-width
        @click="handleConfirm"
      />
      <BaseButton
        size="lg"
        label="취소"
        variant="contained"
        color="grey"
        full-width
        @click="handleCancel"
      />
    </div>
    <div class="mt-size-16">
      <a class="text-font-14 cursor-pointer font-medium underline" @click="handleClickResend"
        >인증번호 재전송</a
      >
    </div>
  </div>
</template>
<script setup lang="ts">
import { EMPTY_VERIFICATION_CODE_ARRAY } from '@template/utils';
import { BaseInput, BaseIcon, BaseButton } from '@template/ui';
import Countdown from '@/components/common/Countdown.vue';
import { useToastStore } from '@/stores/useToastStore';
import { useUserStore } from '@/stores/useUserStore';
import { TOAST_MESSAGES } from '@template/types';
import { authService } from '@/services/api';
import { ref, nextTick } from 'vue';

const userStore = useUserStore();
const toastStore = useToastStore();

const emit = defineEmits(['confirm', 'cancel']);

const maxLength = ref(1);

const inputValues = ref([...EMPTY_VERIFICATION_CODE_ARRAY]);
const inputRefs = ref<any[]>([]);
const countdownRef = ref<InstanceType<typeof Countdown> | null>(null);

// 입력 처리
const handleInput = async (index: number, value: string) => {
  inputValues.value[index] = value;

  // maxLength 도달 시 다음 input으로 포커스 이동
  if (value.length === maxLength.value && index < 5) {
    await nextTick();
    inputRefs.value[index + 1]?.focus();
  }
};

const handleKeydown = async (index: number, event: KeyboardEvent) => {
  if (event.key === 'Backspace' && !inputValues.value[index] && index > 0) {
    await nextTick();
    inputRefs.value[index - 1]?.focus();
  }
};

// 붙여넣기 이벤트 처리
const handlePaste = async (startIndex: number, event: ClipboardEvent) => {
  event.preventDefault();

  // 클립보드에서 텍스트 가져오기
  const pasteData = event.clipboardData?.getData('text') || '';
  const numbers = pasteData.replace(/[^0-9]/g, '');

  if (numbers.length === 0) return;

  // 붙여넣은 숫자를 각 칸에 배치 (최대 6자리, startIndex부터 시작)
  const maxFillCount = Math.min(numbers.length, 6, 6 - startIndex);

  for (let i = 0; i < maxFillCount; i++) {
    const targetIndex = startIndex + i;
    if (targetIndex < 6) {
      inputValues.value[targetIndex] = numbers[i];
    }
  }

  // 마지막으로 채워진 칸의 다음 칸으로 포커스 이동
  const lastFilledIndex = startIndex + maxFillCount - 1;
  if (lastFilledIndex < 5) {
    await nextTick();
    inputRefs.value[lastFilledIndex + 1]?.focus();
  }
};

// 카운트다운 이벤트 처리
const handleCountdownFinished = () => {
  // 유효시간 만료 시 인증번호 입력값 초기화
  inputValues.value = [...EMPTY_VERIFICATION_CODE_ARRAY];
  toastStore.addToast(TOAST_MESSAGES.login.verification_expired);
};

const handleClickResend = async () => {
  try {
    const res = await authService.sendEmailVerificationCode({
      email: userStore.email,
      useType: 'RESET_PASSWORD',
    });
    if (res.status === 'success') {
      toastStore.addToast(TOAST_MESSAGES.login.verification_email_resent);
      // 재전송 시 인증번호 입력값 초기화
      inputValues.value = [...EMPTY_VERIFICATION_CODE_ARRAY];
      // 카운트다운 리셋 및 재시작
      countdownRef.value?.reset();
      countdownRef.value?.start();
    }
  } catch (error) {
    console.error(error);
  }
};

const handleConfirm = async () => {
  try {
    const res = await authService.verifyEmailVerificationCode({
      email: userStore.email,
      verificationCode: inputValues.value.join(''),
      useType: 'RESET_PASSWORD',
    });

    if (res.status === 'success') {
      toastStore.addToast(TOAST_MESSAGES.user.verification_code_confirmed);
      emit('confirm');
    }
  } catch (error) {
    console.error('인증번호 확인 실패:', error);
  }
};

const handleCancel = () => {
  emit('cancel');
};
</script>
